import EstadoPruebaTarjeta from './EstadoPruebaTarjeta';
import { enTrial } from '../lib/planGating';
import { condicionesPruebaTarjeta, DIAS_PRUEBA_TARJETA, VERSION_PRUEBA_TARJETA } from '../lib/pruebaConTarjeta';
import { useEffect, useState } from "react";
import Button from "./foundational/Button";
import { iniciarCheckout, obtenerModoCheckout, condicionesPlan, ofertaPara, membresiaVigente, lineaOpcion } from "../lib/plan";
import { PRODUCTOS_PLUS, PRECIOS_ANUNCIADOS_CLP, precioClp } from "../lib/productosPlus";
import { isNative } from "../lib/platform";

/**
 * Qué membresía se puede contratar (sonda GET al servidor, solo web). Las
 * tres son mensuales con renovación automática (decisión de Sebastián del
 * 17-oct); una sin su plan en Flow no se contrata, nunca cae a pago único.
 * Mientras no se sabe (null) o la sonda falló ('error'), NO se puede pagar.
 * En la app instalada ni se pregunta: ahí no hay venta (skill venta-solo-web).
 */
// `activo` (por defecto true): con false no pregunta, para quien solo la
// necesita en un caso (Tu acceso, durante la prueba).
export function useModoCheckout(activo = true) {
  const [modo, setModo] = useState(null);
  const [intento, setIntento] = useState(0);
  useEffect(() => {
    if (isNative || !activo) return undefined;
    let vivo = true;
    setModo(null);
    obtenerModoCheckout().then((m) => { if (vivo) setModo(m || "error"); });
    return () => { vivo = false; };
  }, [intento, activo]);
  const pausadas = modo?.checkoutEnabled === false;
  const listo = !!modo && modo !== "error" && !pausadas;
  const contratable = (producto) => listo && modo.productos?.[producto]?.recurrente === true;
  const textoCondiciones = (producto) => {
    if (pausadas) return "Las suscripciones estarán disponibles próximamente.";
    if (modo === "error") return "No pudimos cargar las condiciones del pago.";
    if (!listo) return "Cargando las condiciones del pago…";
    if (!contratable(producto)) return `${PRODUCTOS_PLUS[producto]?.nombre ?? "Esta membresía"} todavía no se puede contratar.`;
    return condicionesPlan("mensual", true, producto);
  };
  return { modo, listo, contratable, textoCondiciones, reintentar: () => setIntento((n) => n + 1) };
}

/**
 * El bloque de compra de la web, igual en Planes y en la hoja del límite
 * (un mismo tipo de decisión, un mismo patrón). Desde el 25-oct se venden
 * tres membresías (decisión de Sebastián del 17-oct): se ofrece el Plus de
 * la experiencia de la persona y Full, como la página de precios, con el
 * selector de siempre (el que tenía mensual y semestral). Las condiciones de
 * la opción elegida van ANTES del botón (Términos §6) y salen de lo que el
 * servidor haría de verdad con ESA membresía.
 *
 * `trial*`: la prueba gratis de la experiencia, cuando está disponible.
 * `pie`: lo que va al final (p. ej. "Seguir con
 * el plan gratis"). `productoInicial`: la membresía que la persona eligió
 * antes de llegar (el botón "Contratar Full" de /precios), si se le ofrece.
 */
export default function ContratarMembresia({
  profile, origen, onMetric, onPagoConfirmado, tamano = "lg",
  trialDisponible = false, onTrial, trialLoading = false, errorPrueba = null,
  pie = null, productoInicial = null,
}) {
  const vigente = membresiaVigente(profile);
  const opciones = ofertaPara(profile);
  const [elegido, setElegido] = useState(opciones.includes(productoInicial) ? productoInicial : opciones[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aceptaPrueba, setAceptaPrueba] = useState(false);
  const { modo, contratable, textoCondiciones, reintentar } = useModoCheckout(!vigente);

  // La renovación de una membresía vigente la gestiona Flow. Volver desde
  // /precios no debe ofrecer otra activación, aunque sea del mismo producto.
  // Se calcula en cada render para cubrir también el refresco tras un pago.
  if (vigente) {
    return (
      <div className="flex flex-col gap-2.5" role="status">
        <p className="text-13px text-ink-subtle font-sans leading-relaxed">
          {opciones.includes(vigente)
            ? `Ya tienes ${PRODUCTOS_PLUS[vigente].nombre} vigente. Puedes revisar tu membresía desde Mi perfil.`
            : `Ya tienes ${PRODUCTOS_PLUS[vigente].nombre} vigente. Para cambiarte de membresía, escríbenos a contacto@mipae.cl.`}
        </p>
        {pie}
      </div>
    );
  }
  if (profile?.trial_con_tarjeta && enTrial(profile)) return <EstadoPruebaTarjeta onPagoConfirmado={onPagoConfirmado} />;
  if (!elegido) return null;
  const nombre = PRODUCTOS_PLUS[elegido].nombre;

  const sePuedePagar = contratable(elegido);
  const conPrueba = trialDisponible && !!DIAS_PRUEBA_TARJETA[elegido] && elegido === `plus_${profile?.rol}`;
  const pagar = async (prueba = false) => {
    if (!sePuedePagar || (prueba && (!aceptaPrueba || !modo?.pruebaConTarjeta))) return;
    setLoading(true); setError(null);
    onMetric?.("checkout_iniciado", `${origen}_${elegido}`);
    try {
      window.location.href = await iniciarCheckout("mensual", "recurrente", elegido, ...(prueba ? [{ version: VERSION_PRUEBA_TARJETA, aceptada: true, dias: DIAS_PRUEBA_TARJETA[elegido] }] : []));
    } catch (e) {
      setError(e.message || "Error al procesar el pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  const errorVisible = error || errorPrueba;
  const activar = loading ? "Redirigiendo a Flow…" : `Activar ${nombre}`;
  return (
    <>
      {PRODUCTOS_PLUS[productoInicial]?.disponible === true && !opciones.includes(productoInicial) && (
        <p className="mb-3 text-13px text-ink-subtle font-sans leading-relaxed" role="status">
          Elegiste {PRODUCTOS_PLUS[productoInicial].nombre}. Esa membresía corresponde a otra experiencia. Aquí puedes elegir {PRODUCTOS_PLUS[opciones[0]].nombre} o Full para tu cuenta.
        </p>
      )}
      <div className="flex gap-2.5 mb-4" role="group" aria-label="Elige tu membresía">
        {opciones.map((producto) => {
          const sel = producto === elegido;
          return (
            <button
              key={producto}
              type="button"
              aria-pressed={sel}
              onClick={() => { setElegido(producto); setError(null); setAceptaPrueba(false); }}
              className={`flex-1 min-w-0 rounded-xl p-3.5 text-left transition-colors ${sel ? "border-2 border-accent bg-accent-bg" : "border border-edge"}`}
            >
              <p className="text-11px text-ink-muted font-sans">{PRODUCTOS_PLUS[producto].nombre}</p>
              <p className="font-serif text-lg font-bold text-ink mt-0.5">{precioClp(PRECIOS_ANUNCIADOS_CLP[producto])}</p>
              <p className="text-11px text-ink-muted font-sans">al mes</p>
              <p className="mt-1.5 text-11px text-ink-subtle font-sans leading-snug">{lineaOpcion(producto, profile)}</p>
            </button>
          );
        })}
      </div>

      {/* Condiciones de la opción elegida, antes del botón (Términos §6).
          Cuándo parte el cobro va primero (auditoría de conversión del
          25-oct-2026): al lado de la prueba que no cobra, contratar sí cobra
          ese mismo día, y eso se dice antes del botón. "Si contratas" para que
          no se lea como si la prueba cobrara. */}
      <p className="mb-1 text-12px text-ink-muted font-sans leading-relaxed">
        {sePuedePagar ? (conPrueba ? condicionesPruebaTarjeta(elegido) : `El primer mes se cobra al contratar. ${textoCondiciones(elegido)}`) : textoCondiciones(elegido)}
      </p>
      {/* Solo web: este bloque no se monta en la app instalada. */}
      <a href="/precios" target="_blank" rel="noopener noreferrer" className="mb-3 inline-block text-12px text-accent font-sans underline">
        Ver precio y condiciones
      </a>
      {modo === "error" && (
        <Button variant="secondary" size="sm" onClick={reintentar} className="mb-3">
          Reintentar
        </Button>
      )}

      <div className="flex flex-col gap-2.5">
        {errorVisible && <p className="text-12px text-system-error font-sans text-center">{errorVisible}</p>}
        {conPrueba ? (
          <>
            <label className="flex items-start gap-2 min-h-[44px] text-small text-ink-subtle dark:text-ink-muted font-sans">
              <input type="checkbox" checked={aceptaPrueba} onChange={(e) => setAceptaPrueba(e.target.checked)} className="mt-1" />
              <span>Acepto que, al terminar la prueba, se cobre {precioClp(PRECIOS_ANUNCIADOS_CLP[elegido])} CLP y que la membresía se renueve cada mes. Puedo cancelar antes desde Mi perfil.</span>
            </label>
            <Button variant="primary" size={tamano} onClick={() => pagar(true)} disabled={loading || !sePuedePagar || !modo?.pruebaConTarjeta || !aceptaPrueba}>
              {loading ? "Redirigiendo a Flow…" : `Registrar tarjeta y probar ${DIAS_PRUEBA_TARJETA[elegido]} días gratis`}
            </Button>
            <p className="text-11px text-ink-muted font-sans text-center">
              La prueba comienza cuando Flow confirma el registro. La tarjeta se registra en Flow.
            </p>
          </>
        ) : (
          <Button variant="primary" size={tamano} onClick={() => pagar(false)} disabled={loading || !sePuedePagar}>
            {activar}
          </Button>
        )}
        {pie}
      </div>
    </>
  );
}


